import { UseMutationResult } from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import { setSelectionData } from './setSelectionData';

import { useTrackedMutation as useMutation } from '@ais-ui/performance-tracking';

interface SetSelectionDataParams {
  jobId: string;
  selections: any[];
  show: boolean;
  show_only_selected: boolean;
  show_sog_distribution: boolean;
  show_decimal_knots: boolean;
  ignore_sog_of_zero: boolean;
}

type FetchOverlayData = (jobId: string, hasZoomed: boolean) => void;

export const useSetSelectionData = (
  fetchOverlayData: FetchOverlayData,
): UseMutationResult<string, Error, SetSelectionDataParams> => {
  const { dbSession } = useSession();

  return useMutation(
    {
      mutationFn: async ({
        jobId,
        selections,
        show = false,
        show_only_selected = false,
        show_sog_distribution = false,
        show_decimal_knots = false,
        ignore_sog_of_zero = false,
      }: SetSelectionDataParams) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }

        const params = {
          selections: selections,
          show: show,
          show_only_selected: show_only_selected,
          show_sog_distribution: show_sog_distribution,
          show_decimal_knots: show_decimal_knots,
          ignore_sog_of_zero: ignore_sog_of_zero,
        };

        try {
          const result = await setSelectionData(
            dbSession,
            jobId,
            JSON.stringify(params),
          );
          return String(result);
        } catch (error) {
          // Handle DataNotLoaded errors gracefully - this can happen when switching reports
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          if (
            errorMessage.includes('DataNotLoaded') ||
            errorMessage.includes('InternalError')
          ) {
            console.warn(
              '[useSetSelectionData] Selection data set failed (likely report switch):',
              errorMessage,
            );
            return '';
          }
          throw error;
        }
      },
      onSuccess: (data, variables) => {
        // Only fetch overlay if we got a valid result
        if (data) {
          fetchOverlayData(variables.jobId, true);
        }
      },
    },
    'selection:set_data',
  );
};
